import React from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import clsx from 'clsx';
import { Text } from 'office-ui-fabric-react/lib/Text';
import styles from './FinancialTableRow.style';
import { formatNumberWithCommas } from '../../utils';

const negativeValueClassNameGivenValue = value => {
  return clsx({
    [styles.negativeValue]: value < 0,
  });
};

const parenthesizeIfNegativeAmount = value => {
  if (value > 0) {
    return formatNumberWithCommas(value);
  }

  if (value < 0) {
    return `(${formatNumberWithCommas(Math.abs(value))})`;
  }

  return `${value}`;
};

const FinancialTableRow = ({
  commentId,
  name,
  amount,
  purchasedUnits,
  returnCap,
  capLimit,
  actualReturns,
  returnsOverCap,
  returnsIncluded,
  currency,
  isFooter,
  isDetail,
  isPerCap,
  parenthesize,
  parenthesizeIfNegative,
  href,
  bold,
  className: classNameProp,
  ...other
}) => {
  const classNames = clsx(
    styles.root,
    {
      [styles.footerRow]: isFooter,
    },
    {
      [styles.percapNonDetailRow]: isPerCap && !isDetail,
    },
    {
      [styles.percapFooterRow]: isPerCap && isFooter,
    },
    {
      [styles.bold]: bold,
    },
    classNameProp,
  );
  const detailRowClassName = clsx({
    [styles.details]: isDetail,
  });
  const negativeValueClassName = clsx({
    [styles.negativeValue]: amount < 0,
  });

  const nameText = (
    <>
      {commentId && (
        <Text
          style={{
            fontWeight: 400,
            textAlign: 'super',
            fontSize: '0.75em',
            marginRight: '0.75em',
            verticalAlign: 'super',
          }}
        >
          {commentId}
        </Text>
      )}

      {name}
    </>
  );

  const defaultRenderAmount = () => {
    if (parenthesize || (parenthesizeIfNegative && amount < 0)) {
      return `(${formatNumberWithCommas(Math.abs(amount).toFixed(2))})`;
    }
    return formatNumberWithCommas(amount.toFixed(2));
  };

  return (
    <div className={classNames} {...other}>
      <div className={styles.name}>
        <Text
          as="p"
          variant="medium"
          className={`${detailRowClassName} ${isPerCap && !isDetail ? styles.percapName : ''}`}
        >
          {href ? <a href={href}>{nameText}</a> : nameText}
        </Text>

        {!isNil(purchasedUnits) && (
          <Text as="p" variant="medium" className={styles.purchasedUnits}>
            {formatNumberWithCommas(purchasedUnits)}
          </Text>
        )}
        {!isNil(returnCap) && (
          <Text as="p" variant="medium" className={styles.returnCap}>
            {`${formatNumberWithCommas(returnCap)}%`}
          </Text>
        )}
        {!isNil(capLimit) && (
          <Text as="p" variant="medium" className={styles.capLimit}>
            {formatNumberWithCommas(capLimit)}
          </Text>
        )}
        {!isFooter && isPerCap && (
          <Text as="p" variant="medium" className={styles.actualReturns}>
            {actualReturns ? parenthesizeIfNegativeAmount(actualReturns) : '-'}
          </Text>
        )}
        {!isFooter && isPerCap && (
          <Text as="p" variant="medium" className={styles.returnsOverCap}>
            {returnsOverCap ? parenthesizeIfNegativeAmount(returnsOverCap) : '-'}
          </Text>
        )}
        {!isNil(returnsIncluded) && (
          <Text
            as="p"
            variant="medium"
            className={`${
              isFooter ? styles.footerReturnsIncluded : styles.returnsIncluded
            } ${negativeValueClassNameGivenValue(returnsIncluded)}`}
          >
            {parenthesizeIfNegativeAmount(returnsIncluded)}
          </Text>
        )}
      </div>
      <div className={isFooter ? styles.footerAmount : styles.amount}>
        {isPerCap ? (
          <Text
            as="p"
            variant="medium"
            className={`${detailRowClassName} ${styles.negativeValue} ${styles.percapName}`}
          >
            {!isNil(amount) && `${parenthesizeIfNegativeAmount(amount.toFixed(2))} ${currency}`}
          </Text>
        ) : (
          <Text
            as="p"
            variant="medium"
            className={`${detailRowClassName} ${negativeValueClassName}`}
          >
            {defaultRenderAmount()}
          </Text>
        )}
      </div>
    </div>
  );
};

FinancialTableRow.propTypes = {
  commentId: PropTypes.string,
  name: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  currency: PropTypes.string,
  isFooter: PropTypes.bool,
  isDetail: PropTypes.bool,
  parenthesize: PropTypes.bool,
  parenthesizeIfNegative: PropTypes.bool,
  className: PropTypes.string,
  isPerCap: PropTypes.bool,
  purchasedUnits: PropTypes.number,
  returnCap: PropTypes.number,
  capLimit: PropTypes.number,
  actualReturns: PropTypes.number,
  returnsOverCap: PropTypes.number,
  returnsIncluded: PropTypes.number,
  href: PropTypes.string,
  bold: PropTypes.bool,
};

FinancialTableRow.defaultProps = {
  commentId: null,
  isFooter: false,
  isDetail: false,
  parenthesize: false,
  parenthesizeIfNegative: false,
  className: '',
  currency: '',
  isPerCap: false,
  purchasedUnits: null,
  returnCap: null,
  capLimit: null,
  actualReturns: null,
  returnsOverCap: null,
  returnsIncluded: null,
  href: null,
  bold: false,
};

export default FinancialTableRow;
